import { createAction, createStoreAction } from '@cobuildlab/react-simple-state';
import {
  changeDashboardDateFilterStore,
  changeDateFilterStore,
  fetchUsersCountStore,
  winnerNoteErrorEvents,
  winnerNoteErrorPaginateEvents,
  winnerNoteEvents,
  winnerNotePaginateEvents,
  changeGoogleDateFilterStore,
  selectedSectionStore,
  fetchGenerateUserReportEvent,
  fetchGenerateUserReportErroEvent,
  fetchGenerateWinnerReportEvent,
  fetchGenerateWinnerReportErroEvent,
  fetchCouponReportByFilterEvent,
  fetchCouponReportByFilterEventError,
  fetchCouponRedemptionsListEvent,
  fetchCouponRedemptionsListEventError,
} from './dashboard-events';
import { BACKEND_ENDPOINT, DateFilter } from '../../shared/constants';
import axios from 'axios';
import { CountData, GenerateUserReport, WinnerNote } from './dashboard-types';
import { User } from '../../shared/types/generated';

export const changeDateFilterStoreAction = createStoreAction(
  changeDateFilterStore,
  (prev, date: DateFilter) => ({
    ...prev,
    date,
  }),
);

export const changeDashboardDateFilterStoreAction = createStoreAction(
  changeDashboardDateFilterStore,
  (prev, from: string, to: string, filter: DateFilter) => ({
    ...prev,
    from,
    to,
    filter,
  }),
);

export const winnerNotes = createAction(
  winnerNoteEvents,
  winnerNoteErrorEvents,
  async (data: { from: string; to: string; order: 'asc' | 'desc'; isSubscription?: boolean }) => {
    const { to, from, order, isSubscription } = data;

    const url = isSubscription
      ? `${BACKEND_ENDPOINT}/api/user/subscription/winner-notes`
      : `${BACKEND_ENDPOINT}/api/user/winner-notes`;

    const response = await axios.get<{
      data: WinnerNote[];
      total: number;
    }>(url, {
      params: {
        to,
        from,
        order,
      },
    });

    return response.data;
  },
);

export const winnerNotesPaginate = createAction(
  winnerNotePaginateEvents,
  winnerNoteErrorPaginateEvents,
  async (data: {
    from: string;
    to: string;
    page: number;
    pageSize: number;
    order: 'asc' | 'desc';
    isSubscription?: boolean;
  }) => {
    const { to, from, page, order, pageSize, isSubscription } = data;

    const url = isSubscription
      ? `${BACKEND_ENDPOINT}/api/user/subscription/winner-notes`
      : `${BACKEND_ENDPOINT}/api/user/winner-notes`;

    const response = await axios.get<{
      data: WinnerNote[];
      total: number;
    }>(url, {
      params: {
        to,
        from,
        page,
        order,
        pageSize,
      },
    });

    return response.data;
  },
);

export const fetchUsersCountStoreAction = createStoreAction(
  fetchUsersCountStore,
  (prev, loading: boolean, users: CountData[]) => ({
    ...prev,
    loading,
    users,
  }),
);

export const changeGoogleDateFilterStoreAction = createStoreAction(
  changeGoogleDateFilterStore,
  (prev, from: string, to: string, filter: DateFilter) => ({
    ...prev,
    from,
    to,
    filter,
  }),
);

export const selectedSectionStoreAction = createStoreAction(
  selectedSectionStore,
  (prev, section: string) => ({
    ...prev,
    section,
  }),
);

export const fetchGenerateUserReport = createAction(
  fetchGenerateUserReportEvent,
  fetchGenerateUserReportErroEvent,
  async (data: { user: User | null | undefined; from: string; to: string }) => {
    const { to, from } = data;

    const response = await axios.get<GenerateUserReport[]>(
      `${BACKEND_ENDPOINT}/api/user/generate-user-report`,
      {
        params: {
          requested_by: data.user?.name ?? data.user?.email,
          to,
          from,
        },
      },
    );

    return {
      report: response.data,
    };
  },
);

export const fetchGenerateWinnerReport = createAction(
  fetchGenerateWinnerReportEvent,
  fetchGenerateWinnerReportErroEvent,
  async (data: {
    user: User | null | undefined;
    from: string;
    to: string;
    isSubscription?: boolean;
  }) => {
    const { to, from, isSubscription = false } = data;

    const url = isSubscription
      ? `${BACKEND_ENDPOINT}/api/user/subscription/generate-winner-note-report`
      : `${BACKEND_ENDPOINT}/api/user/generate-winner-note-report`;

    const response = await axios.get<WinnerNote[]>(url, {
      params: {
        requested_by: data.user?.name ?? data.user?.email,
        to,
        from,
      },
    });

    return {
      report: response.data,
    };
  },
);

export const fetchCouponReportByFilter = createAction(
  fetchCouponReportByFilterEvent,
  fetchCouponReportByFilterEventError,
  async (data: { from: string; to: string }) => {
    const { from, to } = data;
    const url = `${BACKEND_ENDPOINT}/api/coupons/group-by-type`;

    try {
      const response = await axios.get(url, {
        timeout: 60000,
        params: {
          to,
          from,
        },
      });

      return response.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? 'Error al obtener los cupones';
      throw new Error(msg);
    }
  },
);

export const fetchCouponRedemptionsList = createAction(
  fetchCouponRedemptionsListEvent,
  fetchCouponRedemptionsListEventError,
  async (data: { from: string; to: string; page: number; pageSize: number }) => {
    const { from, to, page, pageSize } = data;
    const url = `${BACKEND_ENDPOINT}/api/coupons/redemptions`;

    try {
      const response = await axios.get(url, {
        timeout: 60000,
        params: {
          to,
          from,
          page,
          pageSize,
        },
      });

      return response.data;
    } catch (error: any) {
      const msg = error?.response?.data?.message ?? 'Error al obtener los cupones';
      throw new Error(msg);
    }
  },
);
